import React, { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import Notification from "../../components/Notification";
import ActivityNotifications from "../../components/Dashboard/ActivityNotifications";
import Projects from "../../components/Project/Projects";
const { ApiHelper } = require("../../utils/ApiHelper");
const { UPDATE_COMPANY, PROJECT_MANAGER_DASHBOARD_STATS, COMPANY_DETAIL_OF_USER } = require("../../lib/API_KEYS");

const Dashboard = () => {
  const boxBackground = "/images/background.svg";

  const [username, setUsername] = useState("");
  const [projectManagerData, setProjectManagerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptedDismissed, setAcceptedDismissed] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    projects: { total: 0, inExecution: 0, completed: 0 },
  });

  const handleDismissCongratulations = async () => {
    try {
      await new ApiHelper().FetchFromServer(
        UPDATE_COMPANY[0].replace(":companyId", projectManagerData?._id),
        UPDATE_COMPANY[1],
        undefined, { closeMessage: true })
      setAcceptedDismissed(true);
    } catch (err) {
      console.error('Error dismissing congratulations:', err);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const res = await new ApiHelper().FetchFromServer(
        ...PROJECT_MANAGER_DASHBOARD_STATS
      );
      if (res?.data) {
        setDashboardStats(res.data);
      }
    } catch (err) {
      console.error("Error fetching project manager dashboard stats:", err);
    }
  };

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const allProjectsCount = dashboardStats.projects?.total ?? 0;
  const inExecutionCount = dashboardStats.projects?.inExecution ?? 0;
  const completedCount = dashboardStats.projects?.completed ?? 0;

  const today = new Date();
  const formattedDate = today.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const fullName = user.attributes.name;
        const first = fullName.split(" ")[0];
        setUsername(first);
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await new ApiHelper().FetchFromServer(
          ...COMPANY_DETAIL_OF_USER,
        );
        setProjectManagerData(res.data || null);
      } catch (err) {
        console.error("Error fetching project manager info:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sections = [
    {
      title: "Projects",
      items: [
        { label: "All Projects", value: allProjectsCount, icon: "/images/project.svg" },
        { label: "In Execution", value: inExecutionCount, icon: "/images/execution.svg" },
        { label: "Completed", value: completedCount, icon: "/images/completed.svg" },
      ],
    },
  ];

  const renderNotification = () => {
    if (loading) return null;

    if (!projectManagerData?.status || projectManagerData?.status === 'Pending') {
      return (
        <Notification
          variant="empty"
          heading="You need to fill out company information form in order to be reviewed"
          subHeading="The applications not filled out within 24 hours will be deleted automatically"
          link="/manager-dashboard/additional-info"
          text="Fill out Form"
        />
      );
    }

    switch (projectManagerData.status) {
      case "In Review":
        return (
          <Notification
            variant="pending"
            heading="Your application is under consideration"
            subHeading="Your application will be reviewed within 24 hours. Once admin approves or rejects, we'll notify you."
          />
        );

      case "Query Raised":
        return (
          <Notification
            variant="rejected"
            heading="Admin Raised Concerns About Your Application"
            subHeading={projectManagerData.concern || "Please review and update your application information."}
            link="/manager-dashboard/additional-info"
            text="Update Application Info"
            buttonStyle={{ backgroundColor: "#ffc107", color: "white" }}
          />
        );

      case "Rejected":
        return (
          <Notification
            variant="rejected"
            heading="Your application was permanently rejected"
            subHeading={projectManagerData.rejectionReason || "Unfortunately your application was rejected. Please contact support if you believe this was an error."}
          />
        );

      case "Approved":
        return (
          (!projectManagerData?.closeMessage && !acceptedDismissed) && <Notification
            variant="accepted"
            heading="Congratulations! You have been reviewed and approved"
            subHeading="Now you can use our platform as a verified user"
            onClose={handleDismissCongratulations}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {renderNotification()}
      <div className="container my-4">
        <div className="row align-items-center justify-content-between">
          <div className="col-6 d-flex align-items-center">
            <img
              src="/images/hand.svg"
              alt="wave"
              className="me-2"
              style={{ width: "24px", height: "24px" }}
            />
            <h4 className="mb-0" style={{ maxWidth: "100%" }}>
              Hello, {username}
            </h4>
          </div>

          <div className="col-6 d-flex justify-content-end">
            <div
              className="d-inline-flex align-items-center px-3 py-2 text-nowrap flex-shrink-0"
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "50px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <img
                src="/images/calendar.svg"
                alt="Calendar"
                style={{ width: "24px", height: "24px", marginRight: "8px" }}
              />
              <span>{formattedDate}</span>
            </div>
          </div>

          <div className="col-12 mt-2">
            <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
              Your personalized dashboard for smarter control and seamless
              experience
            </p>
          </div>
        </div>
      </div>

      <div className="container-fluid p-4">
        {sections.map((section, sIdx) => (
          <div key={sIdx} className="mb-5">
            <h5 className="mb-3">{section.title}</h5>
            <div className="d-flex flex-wrap" style={{ gap: "1rem" }}>
              {section.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex-fill p-4 rounded-4 border shadow-sm text-start"
                  style={{
                    backgroundImage: `url(${boxBackground})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    minHeight: "150px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <div style={{ position: "relative", zIndex: 1 }}>
                    {item.icon && (
                      <img
                        src={item.icon}
                        alt="icon"
                        style={{ width: "30px", marginBottom: "8px" }}
                      />
                    )}
                    <h4 className="mb-2">{item.value}</h4>
                    <p className="mb-0 text-muted">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="container-fluid px-4 mb-5" id="projects-section">
        <Projects userRole={"Project Manager"} />
      </div>

      <div className="container-fluid px-4">
        <ActivityNotifications userRole="manager" />
      </div>
    </>
  );
};

export default Dashboard;
